import { SafetyEquipment, SafetyHazard } from '../types/ProductTypes';

/**
 * Calculate the maximum exposure level from all safety hazards for a specific exposure type.
 * Returns null if no hazards have a value set (value > -1).
 */
function calculateMaxExposureLevel(hazards: SafetyHazard[], exposureType: 'normal' | 'high' | 'extreme'): number | null {
    if (!hazards || hazards.length === 0) {
        return null;
    }

    let maxValue = -1;

    hazards.forEach((hazard) => {
        let levelValue: number | null = null;

        switch (exposureType) {
            case 'normal':
                levelValue = hazard.normalExposureLevel;
                break;
            case 'high':
                levelValue = hazard.highExposureLevel;
                break;
            case 'extreme':
                levelValue = hazard.extremeExposureLevel;
                break;
        }

        if (levelValue !== null && levelValue > maxValue) {
            maxValue = levelValue;
        }
    });

    return maxValue > -1 ? maxValue : null;
}

/**
 * Calculate exposure levels generated from hazards.
 * Returns the highest value from all safety hazards for each exposure type.
 */
export function calculateHazardExposureLevels(safetyEquipment: SafetyEquipment): {
    normalExposureLevelGenerated: number | null;
    highExposureLevelGenerated: number | null;
    extremeExposureLevelGenerated: number | null;
} {
    const hazards = safetyEquipment.safetyHazards || [];

    return {
        normalExposureLevelGenerated: calculateMaxExposureLevel(hazards, 'normal'),
        highExposureLevelGenerated: calculateMaxExposureLevel(hazards, 'high'),
        extremeExposureLevelGenerated: calculateMaxExposureLevel(hazards, 'extreme'),
    };
}

/**
 * Calculate active protection levels.
 * Returns override value if set, otherwise returns generated value.
 */
export function calculateProtectionLevels(
    safetyEquipment: SafetyEquipment,
    generatedLevels: {
        normalExposureLevelGenerated: number | null;
        highExposureLevelGenerated: number | null;
        extremeExposureLevelGenerated: number | null;
    }
): {
    normalExposureLevelActive: number | null;
    highExposureLevelActive: number | null;
    extremeExposureLevelActive: number | null;
} {
    return {
        normalExposureLevelActive: safetyEquipment.normalExposureLevel ?? generatedLevels.normalExposureLevelGenerated,
        highExposureLevelActive: safetyEquipment.highExposureLevel ?? generatedLevels.highExposureLevelGenerated,
        extremeExposureLevelActive: safetyEquipment.extremeExposureLevel ?? generatedLevels.extremeExposureLevelGenerated,
    };
}

/**
 * Get all calculated exposure levels and active protection levels.
 * This combines both generated and active levels in one call.
 */
export function getExposureLevels(safetyEquipment: SafetyEquipment) {
    const generatedLevels = calculateHazardExposureLevels(safetyEquipment);
    const activeLevels = calculateProtectionLevels(safetyEquipment, generatedLevels);

    return {
        ...generatedLevels,
        ...activeLevels,
    };
}

/**
 * Check if any exposure level has been manually overridden.
 */
export function isManuallyChanged(safetyEquipment: SafetyEquipment): boolean {
    return (
        safetyEquipment.normalExposureLevel !== null ||
        safetyEquipment.highExposureLevel !== null ||
        safetyEquipment.extremeExposureLevel !== null
    );
}

/**
 * Check if any exposure level is N/A (generated value is null when override is also null).
 */
export function hasNALevel(
    safetyEquipment: SafetyEquipment,
    generatedLevels: {
        normalExposureLevelGenerated: number | null;
        highExposureLevelGenerated: number | null;
        extremeExposureLevelGenerated: number | null;
    }
): boolean {
    return (
        (generatedLevels.normalExposureLevelGenerated === null && safetyEquipment.normalExposureLevel === null) ||
        (generatedLevels.highExposureLevelGenerated === null && safetyEquipment.highExposureLevel === null) ||
        (generatedLevels.extremeExposureLevelGenerated === null && safetyEquipment.extremeExposureLevel === null)
    );
}
